/**
 * Chord Inversion Drill — free-practice mode.
 *
 * The chord quality is shown on screen; identify which inversion you heard.
 */

import {
  useReducer,
  useRef,
  useEffect,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoachSessionDoneCard } from "../components/CoachSessionDone.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { ModeSwitcherBar, MODE_COLOR as PICKER_MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { ExerciseLessonLink } from "../components/ExerciseLessonLink.js";

import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { ListenAutoAdvanceControl } from "../components/ListenAutoAdvanceControl.js";
import { nextRoundSeed } from "../lib/round-seed.js";
import {
  buildChordInversionExerciseRound,
  DEFAULT_CHORD_INVERSION_EXERCISE_CONFIG,
  inversionDrillItemId,
  inversionDrillStatsKey,
  INVERSION_CHORD_POOL,
  INVERSION_CHORD_TYPES_ORDERED,
  INVERSION_CHORD_GROUPS,
  INVERSION_NAMES,
  INVERSION_SUBLABELS,
  type ChordInversionExerciseConfig,
  type ChordInversionExerciseRound,
  type InversionChordType,
  type InversionIndex,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import {
  useCardsStore,
  usePrefsStore,
  getDrillStats,
  upsertDrillStats,
  recordReview as dbRecordReview,
  type DrillStatsRecord,
} from "@music-app/persistence";
import { exerciseReviewSource, type Rating } from "@music-app/learning-system";
import { createUISoundPlayer, type UISoundPlayer } from "../lib/ui-sounds.js";
import { ChallengeTimer } from "../components/ChallengeTimer.js";
import { ChallengeScoreCard } from "../components/ChallengeScoreCard.js";
import { useChallengeTimer } from "../lib/use-challenge-timer.js";
import { getChallengeData, recordChallengeAttempt } from "@music-app/persistence";

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

const ALL_INVERSION_INDICES: InversionIndex[] = [0, 1, 2, 3];

const TONIC_OPTIONS: { label: string; midi: number }[] = [
  { label: "C",  midi: 60 }, { label: "G",  midi: 67 },
  { label: "D",  midi: 62 }, { label: "A",  midi: 69 },
  { label: "E",  midi: 64 }, { label: "F",  midi: 65 },
  { label: "B♭", midi: 70 }, { label: "E♭", midi: 63 },
  { label: "A♭", midi: 68 }, { label: "D♭", midi: 61 },
];

// ─── State machine ────────────────────────────────────────────────────────────

type InversionDrillPhase = "config" | "playing" | "awaiting" | "reveal" | "graded" | "done";

interface SessionStat {
  correct: number;
  played: number;
}

type SessionStatKey = `${InversionChordType}|${InversionIndex}`;

interface InversionDrillState {
  phase: InversionDrillPhase;
  config: ChordInversionExerciseConfig;
  sessionLength: number;
  round: ChordInversionExerciseRound | null;
  roundNumber: number;
  correctCount: number;
  selectedInversion: InversionIndex | null;
  wasCorrect: boolean | null;
  wasTimeout: boolean;
  sessionStats: Partial<Record<SessionStatKey, SessionStat>>;
}

type InversionDrillAction =
  | { type: "START"; config: ChordInversionExerciseConfig; sessionLength: number; round: ChordInversionExerciseRound }
  | { type: "PLAYBACK_DONE" }
  | { type: "SELECT"; inversion: InversionIndex }
  | { type: "TIMEOUT" }
  | { type: "NEXT_ROUND"; round: ChordInversionExerciseRound }
  | { type: "SESSION_DONE" }
  | { type: "STOP" };

const INITIAL_STATE: InversionDrillState = {
  phase: "config",
  config: DEFAULT_CHORD_INVERSION_EXERCISE_CONFIG,
  sessionLength: DEFAULT_SESSION_LENGTH,
  round: null,
  roundNumber: 0,
  correctCount: 0,
  selectedInversion: null,
  wasCorrect: null,
  wasTimeout: false,
  sessionStats: {},
};

function makeStatKey(chordType: InversionChordType, inv: InversionIndex): SessionStatKey {
  return `${chordType}|${inv}`;
}

function addStat(
  stats: Partial<Record<SessionStatKey, SessionStat>>,
  chordType: InversionChordType,
  inv: InversionIndex,
  correct: boolean | null,
): Partial<Record<SessionStatKey, SessionStat>> {
  const key = makeStatKey(chordType, inv);
  const prev = stats[key] ?? { correct: 0, played: 0 };
  return {
    ...stats,
    [key]: {
      played: prev.played + 1,
      correct: prev.correct + (correct === true ? 1 : 0),
    },
  };
}

function inversionDrillReducer(state: InversionDrillState, action: InversionDrillAction): InversionDrillState {
  switch (action.type) {
    case "START":
      return {
        ...INITIAL_STATE,
        phase: "playing",
        config: action.config,
        sessionLength: action.sessionLength,
        round: action.round,
        roundNumber: 1,
      };

    case "PLAYBACK_DONE": {
      if (state.phase !== "playing") return state;
      if (state.config.drillMode === "listening" && state.round) {
        return {
          ...state,
          phase: "reveal",
          sessionStats: addStat(state.sessionStats, state.round.chordType, state.round.inversionIndex, null),
        };
      }
      return { ...state, phase: "awaiting" };
    }

    case "SELECT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      const correct = action.inversion === state.round.inversionIndex;
      return {
        ...state,
        phase: "graded",
        selectedInversion: action.inversion,
        wasCorrect: correct,
        wasTimeout: false,
        correctCount: state.correctCount + (correct ? 1 : 0),
        sessionStats: addStat(state.sessionStats, state.round.chordType, state.round.inversionIndex, correct),
      };
    }

    case "TIMEOUT": {
      if (state.phase !== "awaiting" || !state.round) return state;
      return {
        ...state,
        phase: "graded",
        wasCorrect: false,
        wasTimeout: true,
        sessionStats: addStat(state.sessionStats, state.round.chordType, state.round.inversionIndex, false),
      };
    }

    case "NEXT_ROUND":
      return {
        ...state,
        phase: "playing",
        round: action.round,
        roundNumber: state.roundNumber + 1,
        selectedInversion: null,
        wasCorrect: null,
        wasTimeout: false,
      };

    case "SESSION_DONE":
      return { ...state, phase: "done" };

    case "STOP":
      return { ...INITIAL_STATE };
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ChordInversionExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const VALID_MODES: ExerciseMode[] = ["listen", "practice", "review", "challenge"];
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && VALID_MODES.includes(rawMode) ? rawMode : "practice";
  const listenMode = mode === "listen";
  const challengeMode = mode === "challenge";
  const practiceMode = mode === "practice";
  const coached = searchParams.get("coached") === "true";
  const coachItemCount = Math.max(1, parseInt(searchParams.get("itemCount") ?? "10", 10));
  const nodeId = searchParams.get("nodeId") ?? undefined;
  const from = searchParams.get("from") ?? undefined;

  const [state, dispatch] = useReducer(inversionDrillReducer, INITIAL_STATE);
  const playerRef = useRef<AudioPlayer | null>(null);
  const uiSoundRef = useRef<UISoundPlayer | null>(null);

  const challengeStartedAtRef = useRef<string | null>(null);
  const challengeCorrectRef = useRef(0);
  const challengeResponseTimesRef = useRef<number[]>([]);
  const responseStartRef = useRef<number | null>(null);
  const previousBestRef = useRef<number | null | undefined>(undefined);

  const { recordPlay } = useListenSession("inversions", listenMode, nodeId);
  const { recordItem } = useExerciseSession("inversions", mode === "review" ? "practice" : "drill", !listenMode && !challengeMode);

  const applyReview = useCardsStore((s) => s.applyReview);
  const instrument = usePrefsStore((s) => s.instrument);
  const uiSoundsEnabled = usePrefsStore((s) => s.uiSoundsEnabled);

  useEffect(() => {
    const player = createAudioPlayer();
    playerRef.current = player;
    return () => {
      player.stop();
      player.destroy();
      uiSoundRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !instrument) return;
    void (async () => {
      await player.init();
      if (!player.isInstrumentLoaded(instrument)) {
        await player.loadInstrument(instrument);
      }
      player.setInstrument(instrument);
    })();
  }, [instrument]);

  // Challenge mode: load previous best on mount.
  useEffect(() => {
    if (!challengeMode) return;
    challengeStartedAtRef.current = new Date().toISOString();
    challengeCorrectRef.current = 0;
    challengeResponseTimesRef.current = [];
    void getChallengeData("inversions").then((d) => {
      previousBestRef.current = d.best;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challengeMode]);

  // Record response start time when awaiting answer (for all graded modes).
  useEffect(() => {
    if (state.phase !== "awaiting") return;
    responseStartRef.current = Date.now();
  }, [state.phase]);

  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    if (state.config.drillMode === "listening") return;
    const itemId = inversionDrillItemId(state.round.chordType, state.round.inversionIndex);
    const correct = state.wasCorrect === true;
    const responseMs = responseStartRef.current
      ? Date.now() - responseStartRef.current
      : 8000;
    const chosenAnswerId =
      !correct && state.selectedInversion !== null ? String(state.selectedInversion) : null;
    const source = exerciseReviewSource({ challengeMode, fsrsEnabled: state.config.fsrsEnabled });
    void dbRecordReview({
      itemId,
      sessionId: "",
      rating: correct ? "good" : "again",
      correct,
      responseMs,
      chosenAnswerId,
      source,
    });
    if (challengeMode) {
      challengeResponseTimesRef.current.push(responseMs);
      if (correct) {
        challengeCorrectRef.current += 1;
        const rating: Rating = responseMs < 5000 ? "good" : "hard";
        void applyReview(itemId, rating);
      } else {
        void applyReview(itemId, "again");
      }
      return;
    }
    if (!state.config.fsrsEnabled) return;
    void applyReview(itemId, correct ? "good" : "again");
  }, [state.phase, state.round, state.wasCorrect, state.selectedInversion, state.config.fsrsEnabled, state.config.drillMode, challengeMode, applyReview]);

  useEffect(() => {
    if (state.phase !== "graded" || !state.round) return;
    void upsertDrillStats(
      inversionDrillStatsKey(state.round.chordType, state.round.inversionIndex, state.config),
      "graded",
      state.wasCorrect ?? false,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !state.round) return;
    void upsertDrillStats(
      inversionDrillStatsKey(state.round.chordType, state.round.inversionIndex, state.config),
      "listening",
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  useEffect(() => {
    if (state.phase !== "reveal" || !listenMode || !state.round) return;
    recordPlay(inversionDrillItemId(state.round.chordType, state.round.inversionIndex));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.round]);

  // Challenge mode: record attempt on session done.
  useEffect(() => {
    if (state.phase !== "done" || !challengeMode) return;
    const times = challengeResponseTimesRef.current;
    const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
    const correct = challengeCorrectRef.current;
    void recordChallengeAttempt({
      challengeKey: "inversions",
      startedAt: challengeStartedAtRef.current ?? new Date().toISOString(),
      items: state.sessionLength,
      correct,
      accuracy: state.sessionLength > 0 ? correct / state.sessionLength : 0,
      averageResponseMs: Math.round(avgMs),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  // Challenge mode: auto-advance 600ms after timeout.
  useEffect(() => {
    if (!state.wasTimeout) return;
    const id = window.setTimeout(() => nextRound(), 600);
    return () => window.clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.wasTimeout]);

  const playRound = useCallback(
    async (round: ChordInversionExerciseRound, drillMode: ChordInversionExerciseConfig["drillMode"]) => {
      const player = playerRef.current;
      if (!player) return;
      player.stop();
      await player.init();
      const pref = usePrefsStore.getState().instrument;
      if (!player.isInstrumentLoaded(pref)) await player.loadInstrument(pref);
      player.setInstrument(pref);
      await player.play(round.audioPlan);

      if (drillMode === "listening") {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
        await player.play(round.teachAudio);
      }

      recordItem(inversionDrillItemId(round.chordType, round.inversionIndex));
      dispatch({ type: "PLAYBACK_DONE" });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordItem],
  );

  const replay = useCallback(() => {
    const player = playerRef.current;
    if (!player || !state.round) return;
    void player.play(state.round.audioPlan);
  }, [state.round]);

  const startDrill = useCallback(
    (config: ChordInversionExerciseConfig, sessionLength: number) => {
      const round = buildChordInversionExerciseRound(config, nextRoundSeed());
      dispatch({ type: "START", config, sessionLength, round });
      void playRound(round, config.drillMode);
    },
    [playRound],
  );

  const nextRound = useCallback(() => {
    if (!state.round) return;
    if (state.roundNumber >= state.sessionLength) {
      dispatch({ type: "SESSION_DONE" });
      return;
    }
    const round = buildChordInversionExerciseRound(state.config, nextRoundSeed());
    dispatch({ type: "NEXT_ROUND", round });
    void playRound(round, state.config.drillMode);
  }, [state.config, state.round, state.roundNumber, state.sessionLength, playRound]);

  const handleSelect = useCallback(
    (inversion: InversionIndex) => {
      if (!state.round || state.phase !== "awaiting") return;
      const correct = inversion === state.round.inversionIndex;
      dispatch({ type: "SELECT", inversion });
      if (uiSoundsEnabled) {
        if (!uiSoundRef.current) uiSoundRef.current = createUISoundPlayer();
        uiSoundRef.current.play(correct ? "correct" : "wrong");
      }
      if (!correct && state.round.teachAudio) {
        const teachPlan = state.round.teachAudio;
        const player = playerRef.current;
        if (player) {
          window.setTimeout(() => { void player.play(teachPlan); }, 600);
        }
      }
    },
    [state.round, state.phase, uiSoundsEnabled],
  );

  const handleStop = useCallback(() => {
    playerRef.current?.stop();
    dispatch({ type: "STOP" });
  }, []);

  const handleTimeout = useCallback(() => {
    dispatch({ type: "TIMEOUT" });
  }, []);

  const { remainingMs: challengeRemainingMs } = useChallengeTimer({
    active: challengeMode && state.phase === "awaiting",
    timeMs: 8000,
    onTimeout: handleTimeout,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key.toLowerCase() === "r" && state.phase !== "config" && state.phase !== "done") {
        e.preventDefault();
        replay();
        return;
      }
      if (state.phase === "graded" || state.phase === "reveal") {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          nextRound();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.phase, nextRound, replay]);

  const modeColor = PICKER_MODE_COLOR[mode];

  if (state.phase === "config") {
    if (challengeMode) return <InversionDrillChallengeStart onStart={startDrill} />;
    if (coached) return <InversionDrillCoachStart itemCount={coachItemCount} onStart={startDrill} />;
    return <InversionDrillConfigScreen mode={mode} onStart={startDrill} />;
  }

  if (state.phase === "done") {
    if (coached) return <CoachSessionDoneCard />;
    if (challengeMode) {
      const times = challengeResponseTimesRef.current;
      const avgMs = times.length > 0 ? times.reduce((a, b) => a + b, 0) / times.length : 0;
      return (
        <ChallengeScoreCard
          items={state.sessionLength}
          correct={challengeCorrectRef.current}
          averageResponseMs={avgMs}
          previousBest={previousBestRef.current ?? null}
          onAgain={() => startDrill(state.config, state.sessionLength)}
          onStop={handleStop}
        />
      );
    }
    return (
      <InversionDrillSummary
        config={state.config}
        sessionLength={state.sessionLength}
        correctCount={state.correctCount}
        sessionStats={state.sessionStats}
        onAgain={() => startDrill(state.config, state.sessionLength)}
        onStop={handleStop}
      />
    );
  }

  return (
    <InversionDrillLoop
      state={state}
      listenMode={listenMode}
      practiceMode={practiceMode}
      challengeMode={challengeMode}
      challengeRemainingMs={challengeRemainingMs}
      modeColor={modeColor}
      onSelect={handleSelect}
      onNext={nextRound}
      onReplay={replay}
      onStop={handleStop}
      from={from}
    />
  );
}

// ─── Coached listen auto-start ───────────────────────────────────────────────

function InversionDrillCoachStart({
  itemCount,
  onStart,
}: {
  itemCount: number;
  onStart: (config: ChordInversionExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_CHORD_INVERSION_EXERCISE_CONFIG, drillMode: "listening", fsrsEnabled: false }, itemCount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting listen session…</p>
    </div>
  );
}

// ─── Challenge mode auto-start ────────────────────────────────────────────────

function InversionDrillChallengeStart({
  onStart,
}: {
  onStart: (config: ChordInversionExerciseConfig, sessionLength: number) => void;
}): ReactNode {
  useEffect(() => {
    onStart({ ...DEFAULT_CHORD_INVERSION_EXERCISE_CONFIG, drillMode: "graded", fsrsEnabled: false }, 20);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <p className="font-serif italic text-inkSoft">Starting challenge…</p>
    </div>
  );
}

// ─── Listen mode auto-start ───────────────────────────────────────────────────

// ─── Config persistence ───────────────────────────────────────────────────────

const INVERSION_DRILL_LS_KEY = "chord-inversion-drill-config-v2";

interface InversionDrillSavedConfig {
  sessionLength: number;
  mode: ExerciseMode;
  voicing: ChordInversionExerciseConfig["voicing"];
  tonic: ChordInversionExerciseConfig["tonic"];
  fixedTonicMidi: number;
  selectedChordTypes: InversionChordType[];
  selectedInversions: InversionIndex[];
}

const INVERSION_DRILL_DEFAULTS: InversionDrillSavedConfig = {
  sessionLength: DEFAULT_SESSION_LENGTH,
  mode: "practice",
  voicing: "arpeggiated",
  tonic: "random",
  fixedTonicMidi: 60,
  selectedChordTypes: [...DEFAULT_CHORD_INVERSION_EXERCISE_CONFIG.chordTypes],
  selectedInversions: [...DEFAULT_CHORD_INVERSION_EXERCISE_CONFIG.inversions],
};

function loadInversionDrillConfig(): InversionDrillSavedConfig {
  try {
    const raw = localStorage.getItem(INVERSION_DRILL_LS_KEY);
    if (raw) return { ...INVERSION_DRILL_DEFAULTS, ...(JSON.parse(raw) as Partial<InversionDrillSavedConfig>) };
  } catch { /* ignore */ }
  return INVERSION_DRILL_DEFAULTS;
}

function saveInversionDrillConfig(cfg: InversionDrillSavedConfig): void {
  try { localStorage.setItem(INVERSION_DRILL_LS_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
}

// ─── Config screen ────────────────────────────────────────────────────────────

interface InversionDrillConfigScreenProps {
  mode: ExerciseMode;
  onStart: (config: ChordInversionExerciseConfig, sessionLength: number) => void;
}

function InversionDrillConfigScreen({ mode, onStart }: InversionDrillConfigScreenProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const [initConfig] = useState(loadInversionDrillConfig);

  const [sessionLength, setSessionLength] = useState(initConfig.sessionLength);
  const [showCustomLength, setShowCustomLength] = useState(
    !SESSION_LENGTH_PRESETS.includes(initConfig.sessionLength),
  );
  const modeColor = PICKER_MODE_COLOR[mode];
  const [voicing, setVoicing] = useState<ChordInversionExerciseConfig["voicing"]>(initConfig.voicing);
  const [tonic, setTonic] = useState<ChordInversionExerciseConfig["tonic"]>(initConfig.tonic);
  const [fixedTonicMidi, setFixedTonicMidi] = useState(initConfig.fixedTonicMidi);
  const [selectedChordTypes, setSelectedChordTypes] = useState<Set<InversionChordType>>(
    () => new Set(initConfig.selectedChordTypes),
  );
  const [selectedInversions, setSelectedInversions] = useState<Set<InversionIndex>>(
    () => new Set(initConfig.selectedInversions),
  );

  const [historyStats, setHistoryStats] = useState<Partial<Record<string, DrillStatsRecord | null>>>({});

  useEffect(() => {
    const cfgSnap = { tonic, fixedTonicMidi };
    const pairs: Array<[InversionChordType, InversionIndex]> = [];
    for (const ct of selectedChordTypes) {
      const { toneCount } = INVERSION_CHORD_POOL[ct];
      for (const inv of selectedInversions) {
        if (inv < toneCount) pairs.push([ct, inv]);
      }
    }
    void Promise.all(
      pairs.map(([ct, inv]) => {
        const key = inversionDrillStatsKey(ct, inv, cfgSnap);
        return getDrillStats(key).then((s) => [key, s ?? null] as const);
      }),
    ).then((entries) => setHistoryStats(Object.fromEntries(entries)));
  }, [selectedChordTypes, selectedInversions, tonic, fixedTonicMidi]);

  useEffect(() => {
    saveInversionDrillConfig({
      sessionLength, mode, voicing, tonic, fixedTonicMidi,
      selectedChordTypes: Array.from(selectedChordTypes),
      selectedInversions: Array.from(selectedInversions),
    });
  }, [sessionLength, mode, voicing, tonic, fixedTonicMidi, selectedChordTypes, selectedInversions]);

  const toggleChordType = (ct: InversionChordType) => {
    setSelectedChordTypes((prev) => {
      const next = new Set(prev);
      if (next.has(ct)) {
        if (next.size === 1) return prev;
        next.delete(ct);
      } else {
        next.add(ct);
      }
      return next;
    });
  };

  const toggleInversion = (inv: InversionIndex) => {
    setSelectedInversions((prev) => {
      const next = new Set(prev);
      if (next.has(inv)) {
        if (next.size === 1) return prev;
        next.delete(inv);
      } else {
        next.add(inv);
      }
      return next;
    });
  };

  const handleStart = () => {
    if (selectedChordTypes.size === 0 || selectedInversions.size === 0) return;
    onStart(
      {
        chordTypes: INVERSION_CHORD_TYPES_ORDERED.filter((t) => selectedChordTypes.has(t)),
        inversions: ALL_INVERSION_INDICES.filter((i) => selectedInversions.has(i)),
        voicing,
        tonic,
        fixedTonicMidi,
        drillMode: mode === "listen" ? "listening" : "graded",
        fsrsEnabled: mode === "practice",
      },
      sessionLength,
    );
  };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        title="Chord Inversion Recognition"
        subhead="The chord quality is shown — identify the inversion from the bass note."
      />

      <ModeSwitcherBar current={mode} exercisePath="/exercise/inversions" />
      <ExerciseLessonLink nodeIds={["triad-inversions"]} />

      <div className="space-y-5 mb-10">
        <ConfigRow label="Exercises per session">
          <div className="flex items-center gap-3 flex-wrap">
            {SESSION_LENGTH_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={!showCustomLength && sessionLength === n}
                onClick={() => { setSessionLength(n); setShowCustomLength(false); }}
                className={`px-4 py-2 text-sm border transition-colors ${
                  !showCustomLength && sessionLength === n
                    ? ""
                    : "bg-paper text-ink border-ink/20 hover:border-ink"
                }`}
                style={!showCustomLength && sessionLength === n ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
              >
                {n}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={showCustomLength}
              onClick={() => setShowCustomLength(true)}
              className={`px-4 py-2 text-sm border transition-colors ${
                showCustomLength
                  ? ""
                  : "bg-paper text-ink border-ink/20 hover:border-ink"
              }`}
              style={showCustomLength ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
            >
              Other…
            </button>
            {showCustomLength && (
              <input
                type="number"
                min={1}
                max={50}
                value={sessionLength}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 50) setSessionLength(v);
                }}
                className="w-16 px-3 py-2 text-sm border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
                aria-label="Custom number of exercises"
                autoFocus
              />
            )}
          </div>
        </ConfigRow>

        <ConfigRow label="Voicing">
          <NativeSelect
            value={voicing}
            onChange={(v) => setVoicing(v as ChordInversionExerciseConfig["voicing"])}
            options={[
              { value: "arpeggiated", label: "Arpeggiated  (arp → block)" },
              { value: "blocked",     label: "Blocked  (block only)" },
              { value: "mixed",       label: "Mixed  (random each round)" },
            ]}
          />
        </ConfigRow>

        <ConfigRow label="Root">
          <div className="flex gap-3 flex-wrap items-center">
            <NativeSelect
              value={tonic}
              onChange={(v) => setTonic(v as ChordInversionExerciseConfig["tonic"])}
              options={[
                { value: "random", label: "Random" },
                { value: "fixed",  label: "Fixed" },
              ]}
            />
            {tonic === "fixed" && (
              <NativeSelect
                value={String(fixedTonicMidi)}
                onChange={(v) => setFixedTonicMidi(Number(v))}
                options={TONIC_OPTIONS.map((t) => ({ value: String(t.midi), label: t.label }))}
              />
            )}
          </div>
        </ConfigRow>
      </div>

      {/* Inversion pool */}
      <div className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Inversions to drill <span className="text-inkFaded/60">(minimum 1)</span>
        </p>
        <div className="flex gap-2 flex-wrap">
          {ALL_INVERSION_INDICES.map((inv) => {
            const checked = selectedInversions.has(inv);
            const wouldViolateMin = checked && selectedInversions.size <= 1;
            return (
              <button
                key={inv}
                type="button"
                aria-pressed={checked}
                disabled={wouldViolateMin}
                onClick={() => toggleInversion(inv)}
                className={`px-4 py-2.5 border text-left transition-colors ${
                  inv === 3 ? "opacity-60" : ""
                } ${
                  checked
                    ? ""
                    : "bg-paper text-ink border-ink/20 hover:border-ink"
                } ${wouldViolateMin ? "opacity-30 cursor-not-allowed" : ""}`}
                style={checked ? { backgroundColor: modeColor, color: "#F5F0E8", borderColor: modeColor } : undefined}
              >
                <div className="font-serif text-sm font-medium">{INVERSION_NAMES[inv]}</div>
                <div className="font-mono text-[10px] text-inkFaded mt-0.5">
                  {INVERSION_SUBLABELS[inv]}
                  {inv === 3 && <span className="ml-1">(7th chords only)</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chord type pool */}
      <div className="mb-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Chord types
        </p>

        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            type="button"
            onClick={() => setSelectedChordTypes(new Set(["maj", "min"] as InversionChordType[]))}
            className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
          >
            Maj + Min
          </button>
          <button
            type="button"
            onClick={() => setSelectedChordTypes(new Set(INVERSION_CHORD_TYPES_ORDERED.filter(t => INVERSION_CHORD_POOL[t].group === "triads")))}
            className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
          >
            All triads
          </button>
          <button
            type="button"
            onClick={() => setSelectedChordTypes(new Set(INVERSION_CHORD_TYPES_ORDERED))}
            className="px-3 py-1.5 text-xs font-mono border border-ink/20 text-ink hover:border-ink transition-colors"
          >
            All
          </button>
        </div>

        <div className="border border-ink/10">
          {INVERSION_CHORD_GROUPS.map((group) => (
            <div key={group.label} className="border-b border-ink/10 last:border-b-0">
              <div className="px-4 py-1.5 bg-paper border-b border-ink/5">
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">{group.label}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3">
                {group.symbols.map((ct) => {
                  const checked = selectedChordTypes.has(ct);
                  const info = INVERSION_CHORD_POOL[ct];
                  return (
                    <label
                      key={ct}
                      className={`flex items-center gap-2.5 px-4 py-2.5 cursor-pointer transition-colors ${
                        checked ? "text-ink" : "text-inkFaded"
                      } hover:bg-ink/5`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleChordType(ct)}
                        className="accent-ink w-3.5 h-3.5 shrink-0"
                        aria-label={`Include ${info.longName}`}
                      />
                      <span className="font-mono text-xs font-medium">{info.shortSymbol}</span>
                      <span className="font-serif italic text-xs">{info.longName}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* History table */}
      <InversionHistoryTable
        chordTypes={INVERSION_CHORD_TYPES_ORDERED.filter((t) => selectedChordTypes.has(t))}
        inversions={ALL_INVERSION_INDICES.filter((i) => selectedInversions.has(i))}
        tonicConfig={{ tonic, fixedTonicMidi }}
        historyStats={historyStats}
      />

      <button
        type="button"
        onClick={handleStart}
        disabled={selectedChordTypes.size === 0 || selectedInversions.size === 0}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: modeColor }}
      >
        Start →
      </button>
    </div>
  );
}

// ─── Drill loop ───────────────────────────────────────────────────────────────

interface InversionDrillLoopProps {
  state: InversionDrillState;
  listenMode: boolean;
  practiceMode: boolean;
  challengeMode: boolean;
  challengeRemainingMs: number;
  modeColor: string;
  onSelect: (inversion: InversionIndex) => void;
  onNext: () => void;
  onReplay: () => void;
  onStop: () => void;
  from?: string;
}

function InversionDrillLoop({
  state,
  listenMode,
  practiceMode,
  challengeMode,
  challengeRemainingMs,
  modeColor,
  onSelect,
  onNext,
  onReplay,
  onStop,
  from,
}: InversionDrillLoopProps): ReactNode {
  const { phase, round, roundNumber, sessionLength, config, selectedInversion, wasCorrect, wasTimeout } = state;

  const isPlaying  = phase === "playing";
  const isAwaiting = phase === "awaiting";
  const isReveal   = phase === "reveal";
  const isGraded   = phase === "graded";

  const isLastRound = roundNumber >= sessionLength;
  const answeredCount = isGraded || isReveal ? roundNumber : roundNumber - 1;

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <div className="flex items-center gap-3 mb-4">
        {from === "today" && (
          <>
            <Link
              to="/"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Today
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {from === "learn" && (
          <>
            <Link
              to="/learn"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Learn
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        {!from && (
          <>
            <Link
              to="/exercise"
              onClick={onStop}
              className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
            >
              ← Ear Training
            </Link>
            <span className="font-mono text-[10px] text-inkFaded/40">·</span>
          </>
        )}
        <button
          type="button"
          onClick={onStop}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors"
          aria-label="Return to drill settings"
        >
          ← Settings
        </button>
      </div>
      <ModeSwitcherBar current={challengeMode ? "challenge" : practiceMode ? "review" : listenMode ? "listen" : "practice"} />

      {challengeMode && isAwaiting && (
        <ChallengeTimer totalMs={8000} remainingMs={challengeRemainingMs} />
      )}

      <div className="flex items-baseline justify-between mb-1">
        <h1 className="font-serif text-4xl font-medium text-ink">Chord Inversion Drill</h1>
        <p className="font-mono text-xs text-inkFaded tabular-nums" aria-label={`Exercise ${roundNumber} of ${sessionLength}`}>
          {roundNumber} of {sessionLength}
        </p>
      </div>
      <p className="font-mono text-xs text-inkFaded mb-4">
        {config.voicing === "mixed" ? "Mixed voicing" : config.voicing}
        {" · "}
        {config.tonic === "random" ? "random root" : "fixed root"}
      </p>

      <div className="w-full h-0.5 bg-ink/10 mb-2" role="progressbar" aria-valuenow={roundNumber} aria-valuemin={1} aria-valuemax={sessionLength}>
        <div className="h-full transition-all duration-300" style={{ width: `${(roundNumber / sessionLength) * 100}%`, backgroundColor: modeColor }} />
      </div>

      <div className="flex justify-between font-mono text-xs text-inkFaded mb-6">
        {config.drillMode !== "listening" ? (
          <span>{state.correctCount} correct of {answeredCount} answered</span>
        ) : (
          <span>{answeredCount} of {sessionLength} listened</span>
        )}
        {isPlaying && <span className="animate-pulse">Playing…</span>}
      </div>

      {/* Chord quality badge — always shown so the user focuses on inversion */}
      {round && (
        <div className="mb-6 flex items-center gap-3">
          <span className="font-mono text-xs text-inkFaded uppercase tracking-[0.18em]">Chord:</span>
          <span className="font-serif text-xl font-medium text-ink">
            {INVERSION_CHORD_POOL[round.chordType].longName}
          </span>
          <span className="font-mono text-sm text-inkFaded">
            {INVERSION_CHORD_POOL[round.chordType].shortSymbol}
          </span>
        </div>
      )}

      {/* Prompt / reveal */}
      <div className="mb-6 min-h-[3rem] flex items-center">
        {isPlaying && (
          <p className="font-serif italic text-xl text-inkSoft" aria-live="polite">Listen…</p>
        )}
        {isAwaiting && (
          <p className="font-serif italic text-xl text-ink" aria-live="polite">
            Which inversion is this?
          </p>
        )}
        {(isReveal || isGraded) && round && (
          <div aria-live="polite">
            {isReveal && (
              <p className="font-serif italic text-xl text-ink">
                That was{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {INVERSION_NAMES[round.inversionIndex]}
                </strong>
                {" "}—{" "}
                <span className="text-inkSoft">{INVERSION_SUBLABELS[round.inversionIndex]}</span>.
              </p>
            )}
            {isGraded && wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                Correct —{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {INVERSION_NAMES[round.inversionIndex]}
                </strong>
                .
              </p>
            )}
            {isGraded && !wasCorrect && (
              <p className="font-serif italic text-xl text-ink">
                {wasTimeout ? "Time! " : "The answer was "}{" "}
                <strong className="not-italic font-medium" style={{ color: modeColor }}>
                  {INVERSION_NAMES[round.inversionIndex]}
                </strong>
                {" "}({INVERSION_SUBLABELS[round.inversionIndex]}).
              </p>
            )}
            {isGraded && !challengeMode && (
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1"
                 style={{ color: practiceMode ? modeColor : "#9A9390" }}>
                {practiceMode ? "Counts toward Today." : "No schedule changes."}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Answer buttons — always show all valid inversions for the chord type */}
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-2 mb-2">
          {(round?.answerInversions ?? ([0, 1, 2] as InversionIndex[])).map((inv) => {
            const isCorrect  = inv === round?.inversionIndex;
            const isSelected = inv === selectedInversion;
            const showCorrect = (isGraded || isReveal) && isCorrect;
            const showWrong   = isGraded && isSelected && !isCorrect;
            const disabled    = isPlaying || isReveal || isGraded;

            return (
              <button
                key={inv}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(inv)}
                aria-pressed={isSelected && isAwaiting}
                aria-label={`${INVERSION_NAMES[inv]}, ${INVERSION_SUBLABELS[inv]}`}
                className={`px-3 py-3 border text-left transition-colors ${
                  showCorrect
                    ? "bg-success/10 border-success text-success"
                    : showWrong
                    ? "bg-danger/10 border-danger text-danger"
                    : disabled
                    ? "bg-paper border-ink/10 text-inkFaded"
                    : "bg-paper border-ink/20 text-ink hover:border-ink hover:bg-ink/5"
                }`}
              >
                <div className="font-serif text-sm font-medium leading-tight">{INVERSION_NAMES[inv]}</div>
                <div className="font-mono text-[10px] text-inkFaded mt-0.5">{INVERSION_SUBLABELS[inv]}</div>
              </button>
            );
          })}
        </div>
        {isAwaiting && <p className="mt-2 font-mono text-[10px] text-inkFaded">R to replay</p>}
        {(isReveal || isGraded) && (
          <p className="mt-2 font-mono text-[10px] text-inkFaded">
            {isLastRound ? "Space/Enter to finish" : "Space/Enter for next"} · R
          </p>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        {!challengeMode && (
          <button
            type="button"
            disabled={!isReveal && !isGraded}
            onClick={onNext}
            className={buttonClasses("primary")}
            style={{ backgroundColor: modeColor }}
          >
            {isLastRound ? "Finish →" : "Next →"}
          </button>
        )}
        {!challengeMode && (
          <button
            type="button"
            disabled={isPlaying}
            onClick={onReplay}
            className={buttonClasses("secondary")}
            aria-label="Replay the chord"
          >
            ♩ Replay
          </button>
        )}
      </div>
      {listenMode && <ListenAutoAdvanceControl isReveal={isReveal} onAdvance={onNext} />}
    </div>
  );
}

// ─── Session summary ──────────────────────────────────────────────────────────

interface InversionDrillSummaryProps {
  config: ChordInversionExerciseConfig;
  sessionLength: number;
  correctCount: number;
  sessionStats: Partial<Record<SessionStatKey, SessionStat>>;
  onAgain: () => void;
  onStop: () => void;
}

function InversionDrillSummary({
  config,
  sessionLength,
  correctCount,
  sessionStats,
  onAgain,
  onStop,
}: InversionDrillSummaryProps): ReactNode {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from") ?? undefined;
  const isGraded = config.drillMode === "graded";
  const accuracy = isGraded ? Math.round((correctCount / sessionLength) * 100) : null;

  const statRows: Array<{ label: string; sublabel: string; key: SessionStatKey }> = [];
  for (const ct of INVERSION_CHORD_TYPES_ORDERED) {
    const { toneCount } = INVERSION_CHORD_POOL[ct];
    for (let inv = 0; inv < toneCount; inv++) {
      const key = makeStatKey(ct, inv as InversionIndex);
      if (sessionStats[key] !== undefined) {
        statRows.push({
          label: `${INVERSION_CHORD_POOL[ct].longName} — ${INVERSION_NAMES[inv as InversionIndex]}`,
          sublabel: INVERSION_SUBLABELS[inv as InversionIndex],
          key,
        });
      }
    }
  }

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={from === "learn" ? { label: "← Learn", href: "/learn" } : { label: "← Ear Training", href: "/exercise" }}
        eyebrow="chord inversion"
        title="Session complete"
      />

      <div className="grid grid-cols-2 border border-ink/10 mb-8">
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Exercises</p>
          <p className="font-serif text-3xl font-medium text-ink">{sessionLength}</p>
        </div>
        {accuracy !== null && (
          <div className="p-6 border-l border-ink/10">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-1">Correct</p>
            <p className="font-serif text-3xl font-medium text-ink">
              {accuracy}<span className="text-lg text-inkSoft font-serif italic ml-0.5">%</span>
            </p>
          </div>
        )}
      </div>

      {statRows.length > 0 && (
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">By chord + inversion</p>
          <div className="border border-ink/10">
            <div className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2 border-b border-ink/10">
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Chord</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Played</span>
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>}
              {isGraded && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>}
            </div>
            {statRows.map(({ label, sublabel, key }) => {
              const stat = sessionStats[key]!;
              const pct = stat.played > 0 && stat.correct > 0
                ? Math.round((stat.correct / stat.played) * 100)
                : stat.played > 0 ? 0 : null;
              return (
                <div key={key} className="grid grid-cols-[1fr_5rem_5rem_3.5rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0">
                  <div>
                    <span className="font-serif text-sm text-ink">{label}</span>
                    <span className="block font-mono text-[10px] text-inkFaded">{sublabel}</span>
                  </div>
                  <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.played}</span>
                  {isGraded && <span className="font-mono text-xs text-ink text-right tabular-nums">{stat.correct}</span>}
                  {isGraded && (
                    <span className="font-mono text-xs text-inkFaded text-right tabular-nums">
                      {pct !== null ? `${pct}%` : "—"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <button type="button" onClick={onAgain} className={buttonClasses("primary")}>
          Drill again →
        </button>
        <button type="button" onClick={onStop} className={buttonClasses("secondary")}>
          Change settings
        </button>
        <Link to="/exercise" className={buttonClasses("secondary")}>
          All drills
        </Link>
        <Link to="/stats" className={buttonClasses("secondary")}>
          See your stats →
        </Link>
        <ExerciseLessonLink nodeIds={["triad-inversions"]} />
      </div>
    </div>
  );
}

// ─── History table ────────────────────────────────────────────────────────────

interface InversionHistoryTableProps {
  chordTypes: readonly InversionChordType[];
  inversions: readonly InversionIndex[];
  tonicConfig: Pick<ChordInversionExerciseConfig, "tonic" | "fixedTonicMidi">;
  historyStats: Partial<Record<string, DrillStatsRecord | null>>;
}

function InversionHistoryTable({ chordTypes, inversions, tonicConfig, historyStats }: InversionHistoryTableProps): ReactNode {
  const rows: Array<{ ct: InversionChordType; inv: InversionIndex; stats: DrillStatsRecord }> = [];
  for (const ct of chordTypes) {
    const { toneCount } = INVERSION_CHORD_POOL[ct];
    for (const inv of inversions) {
      if (inv >= toneCount) continue;
      const key = inversionDrillStatsKey(ct, inv, tonicConfig);
      const stats = historyStats[key] ?? null;
      if (stats && stats.played > 0) rows.push({ ct, inv, stats });
    }
  }

  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  return (
    <div className="mb-8">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded hover:text-ink transition-colors mb-3"
        aria-expanded={open}
      >
        History
        <span aria-hidden className="text-[8px]">{open ? "▴" : "▾"}</span>
      </button>
      <div className={open ? "border border-ink/10" : "hidden"}>
        <div className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2 border-b border-ink/10">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded">Chord</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Listen</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Practice</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">Correct</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded text-right">%</span>
        </div>
        {rows.map(({ ct, inv, stats: s }) => {
          const listened = Math.max(0, s.played - s.correct - s.errors);
          const graded   = s.correct + s.errors;
          const pct      = graded > 0 ? Math.round((s.correct / graded) * 100) : null;
          const info = INVERSION_CHORD_POOL[ct];
          return (
            <div key={`${ct}|${inv}`} className="grid grid-cols-[1fr_4rem_4rem_4rem_3rem] px-4 py-2.5 border-b border-ink/10 last:border-b-0">
              <div>
                <span className="font-serif text-sm text-ink">{info.longName}</span>
                <span className="font-mono text-xs text-inkFaded ml-2">{INVERSION_NAMES[inv]}</span>
              </div>
              <span className="font-mono text-xs text-ink text-right tabular-nums">{listened}</span>
              <span className="font-mono text-xs text-ink text-right tabular-nums">{graded}</span>
              <span className="font-mono text-xs text-ink text-right tabular-nums">{graded > 0 ? s.correct : "—"}</span>
              <span className="font-mono text-xs text-inkFaded text-right tabular-nums">{pct !== null ? `${pct}%` : "—"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function ConfigRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">{label}</p>
      {children}
    </div>
  );
}

function NativeSelect({
  value, onChange, options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}): ReactNode {
  return (
    <div className="relative inline-block">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-3 pr-8 py-2 bg-paper border border-ink/20 font-mono text-sm text-ink focus:outline-none focus:border-ink appearance-none cursor-pointer"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-inkFaded text-[10px]">▾</span>
    </div>
  );
}
